import type { ColumnFiltersState } from '@tanstack/react-table';

/**
 * Props for the DataTable.Filter configuration component.
 *
 * Supports two modes:
 * - **Controlled**: Use `value` + `onChange` - parent owns the state
 * - **Uncontrolled**: Use `initial` + optional `onChange` - table owns the state
 */
export interface FilterProps {
  /**
   * Controlled mode: Current filter state.
   * When provided, the table syncs to this value on every render.
   * Parent is the source of truth.
   *
   * @example
   * ```tsx
   * const [filters, setFilters] = useState<ColumnFiltersState>([]);
   * <DataTable.Filter value={filters} onChange={setFilters} />
   * ```
   */
  value?: ColumnFiltersState;

  /**
   * Uncontrolled mode: Initial filter state applied on mount.
   * Only used when `value` is not provided.
   * Table manages its own state after mount.
   *
   * @example
   * ```tsx
   * <DataTable.Filter initial={[{ id: 'status', value: 'active' }]} />
   * ```
   */
  initial?: ColumnFiltersState;

  /**
   * Called when filters change from any source (user interaction or programmatic).
   * Works in both controlled and uncontrolled modes.
   *
   * @example
   * ```tsx
   * <DataTable.Filter onChange={(filters) => syncToUrl(filters)} />
   * ```
   */
  onChange?: (filters: ColumnFiltersState) => void;
}

/**
 * Configuration component for external filter state management.
 *
 * This is a declarative config component that returns null.
 * It is parsed by the table-config-parser to extract filter configuration.
 *
 * @example Uncontrolled with initial filters
 * ```tsx
 * <DataTable id="users" data={users}>
 *   <DataTable.Filter initial={[{ id: 'status', value: 'active' }]} />
 *   <DataTable.Column id="status" accessor="status" filterType="select" />
 * </DataTable>
 * ```
 *
 * @example Controlled mode (parent owns state)
 * ```tsx
 * const [filters, setFilters] = useState<ColumnFiltersState>([]);
 *
 * <DataTable id="users" data={users}>
 *   <DataTable.Filter value={filters} onChange={setFilters} />
 *   <DataTable.Column id="status" accessor="status" filterType="select" />
 * </DataTable>
 * ```
 *
 * @example URL sync (controlled)
 * ```tsx
 * const filters = parseFiltersFromUrl(searchParams);
 *
 * <DataTable id="users" data={users}>
 *   <DataTable.Filter
 *     value={filters}
 *     onChange={(f) => setSearchParams({ filters: JSON.stringify(f) })}
 *   />
 * </DataTable>
 * ```
 */
export const Filter = (props: FilterProps): null => {
  if (import.meta.env.DEV) {
    if (props.value !== undefined && props.initial !== undefined) {
      console.warn(
        'DataTable.Filter: `value` and `initial` are mutually exclusive. ' +
          'Use `value` for controlled mode or `initial` for uncontrolled mode, not both.'
      );
    }
    if (props.value !== undefined && props.onChange === undefined) {
      console.warn(
        'DataTable.Filter: Using `value` without `onChange` makes the filter state read-only. ' +
          'User interactions will not update filters. Provide an `onChange` handler.'
      );
    }
  }
  return null;
};
Filter.displayName = 'DataTable.Filter';
